import Axios from '../../../../networking/intraceptor'
import axios from 'axios'

import {
    payroll_otp_request,
    payroll_otp_verify,
    financial_otp_request,
    financial_otp_verify,
    unified_otp_request,
    unified_otp_verify,
    finance_create_business,
    finance_get_businesses,
    finance_accessible_businesses,
    finance_update_business,
    finance_export_to_bookkeeper,
    finance_import_expenses,
    finance_logout,
    finance_delete_bank,
    finance_transactions,
    finance_transactions_chart_data,
    finance_transactions_export_csv,
    finance_transactions_update_memo,
    finance_bills,
    finance_bills_attach_to_transaction,
    paycheck_companies,
    paycheck_companies_list,
    paycheck_companies_by_id,
    get_paycheck_company_details,
    paycheck_companies_export_to_finance,
    authorized_phones,
    delete_authorized_phone,
    payroll_templates,
    create_payroll_checks,
    link_pharmacy_share_paychecks,
    get_linked_pharmacies,
    get_linked_pharmacies_overview,
    unlink_pharmacy_share_paychecks,
    check_users,
    check_users_by_company,
    payroll_checks_list,
    update_payroll_check_status,
    generate_payroll_pdf,
    get_last_check_no,
    reissue_payroll_check,
    import_payroll_csv,
    check_bookkeeper_module_access,
    get_user_by_token,
    update_check_user_by_token,
    get_payee_names,
    extract_payroll_pdf,
    submit_payroll_signature,
    send_signature_link,
    check_user_by_sign_token,
    accounting_access_form,
    finance_current_user,
    finance_linked_pharmacies_direct,
    finance_link_pharmacy,
    finance_unlink_pharmacy
} from '../../../../networking/urlEndPoints'

/**
 * Request OTP for admin verification
 * @param {string} phone - Phone number (10 digits)
 * @returns {Promise} API response
 */
export const requestOTP = async (phone) => {
    try {
        const response = await Axios.post(payroll_otp_request, {
            phone: phone
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Verify OTP for admin verification
 * @param {string} phone - Phone number (10 digits)
 * @param {string} otp - OTP code (6 digits)
 * @returns {Promise} API response
 */
export const verifyOTP = async (phone, otp) => {
    try {
        const response = await Axios.post(payroll_otp_verify, {
            phone: phone,
            otp: otp
        })
        return response
    } catch (error) {
        throw error
    }
}


/**
 * Create paycheck company
 * @param {FormData} formData - FormData object containing company and bank details
 * @returns {Promise} API response
 */
export const createPaycheckCompany = async (formData) => {
    try {
        // Content-Type header will be set automatically by the interceptor for FormData on bookkeepers page
        const response = await Axios.post(paycheck_companies, formData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Update paycheck company
 * @param {number} companyId - Company ID
 * @param {FormData} formData - Form data with company details
 * @returns {Promise} API response
 */
export const updatePaycheckCompany = async (companyId, formData) => {
    try {
        // Content-Type header will be set automatically by the interceptor for FormData on bookkeepers page
        const response = await Axios.post(`${paycheck_companies_by_id}/${companyId}`, formData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Export paycheck company to finance
 * @param {Object} payload - Export payload with business and bank details
 * @returns {Promise} API response
 */
export const exportPaycheckCompanyToFinance = async (payload) => {
    try {
        const response = await Axios.post(paycheck_companies_export_to_finance, payload, {
            headers: {
                'Content-Type': 'application/json'
            }
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Delete paycheck company (soft delete)
 * @param {number} companyId - Company ID to delete
 * @returns {Promise} API response
 */
export const deletePaycheckCompany = async (companyId) => {
    try {
        const response = await Axios.delete(`${paycheck_companies_by_id}/${companyId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Fetch paycheck companies list
 * @returns {Promise} API response
 */
export const fetchPaycheckCompanies = async () => {
    try {
        const response = await Axios.get(paycheck_companies_list)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Fetch company details by company ID
 * @param {number} companyId - Company ID
 * @returns {Promise} API response
 */
export const fetchPaycheckCompanyDetails = async (companyId) => {
    try {
        const response = await Axios.get(`${get_paycheck_company_details}/${companyId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Upload CSV file for a specific company
 * @param {number} companyId - Company ID
 * @param {File} csvFile - CSV file to upload (.csv or .txt)
 * @returns {Promise} API response
 */
export const uploadCompanyCSV = async (companyId, csvFile) => {
    try {
        const formData = new FormData()
        formData.append('company_id', companyId)
        formData.append('csv', csvFile)
        formData.append('template_name', 'Medidoze Standard Format')
        // Content-Type header will be set automatically by the interceptor for FormData on bookkeepers page
        const response = await Axios.post(import_payroll_csv, formData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Create authorized phone number
 * @param {string} phone - Phone number (10 digits)
 * @param {string} name - Name of the authorized person
 * @param {Array<number>} companyIds - Array of company IDs (optional)
 * @returns {Promise} API response
 */
export const createAuthorizedPhone = async (formData) => {
    try {
        // formData is already a FormData object with phone, name, company_ids, and optionally signature_upload
        // Content-Type header will be set automatically by the interceptor for FormData on bookkeepers page
        const response = await Axios.post(authorized_phones, formData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Delete authorized phone number (registered user)
 * @param {number} userId - User ID to delete
 * @returns {Promise} API response
 */
export const deleteAuthorizedPhone = async (userId) => {
    try {
        const response = await Axios.delete(`${delete_authorized_phone}/${userId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Fetch payroll templates list
 * @returns {Promise} API response
 */
export const fetchPayrollTemplates = async () => {
    try {
        const response = await Axios.get(payroll_templates)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Create payroll checks
 * @param {Array} payrollchecks - Array of payroll check objects
 * @returns {Promise} API response
 */
export const createPayrollChecks = async (payrollchecks) => {
    try {
        const response = await Axios.post(create_payroll_checks, {
            payrollchecks: payrollchecks
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Search pharmacies
 * @param {string} searchQuery - Search query string
 * @returns {Promise} API response
 */
export const searchPharmacies = async (searchQuery) => {
    try {
        const response = await Axios.get('open/premium-pharmacies', {
            params: {
                search: searchQuery
            }
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Search banks by bank name
 * @param {string} searchQuery - Search query string (bank name)
 * @returns {Promise} API response
 */
export const searchBanks = async (searchQuery) => {
    try {
        const response = await Axios.get('checks/paycheck-companies/banks-by-institution-number', {
            params: {
                search: searchQuery
            }
        })
        return response
    } catch (error) {
        throw error
    }
}


/**
 * Check bookkeeper module access
 * @returns {Promise} API response with has_access boolean
 */
export const checkBookkeeperModuleAccess = async () => {
    try {
        const response = await Axios.get(check_bookkeeper_module_access)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Link pharmacy for sharing paychecks
 * @param {number} targetPharmacyId - Target pharmacy ID to link with
 * @returns {Promise} API response
 */
export const linkPharmacySharePaychecks = async (targetPharmacyId) => {
    try {
        const response = await Axios.post(link_pharmacy_share_paychecks, {
            target_pharmacy_id: targetPharmacyId
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get linked pharmacies list
 * @returns {Promise} API response
 */
export const fetchLinkedPharmacies = async () => {
    try {
        const response = await Axios.get(get_linked_pharmacies)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get linked pharmacies list for overview tab
 * @returns {Promise} API response
 */
export const fetchLinkedPharmaciesOverview = async () => {
    try {
        const response = await Axios.get(get_linked_pharmacies_overview)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Unlink pharmacy for sharing paychecks
 * @param {number} targetPharmacyId - Target pharmacy ID to unlink
 * @returns {Promise} API response
 */
export const unlinkPharmacySharePaychecks = async (targetPharmacyId) => {
    try {
        const response = await Axios.post(unlink_pharmacy_share_paychecks, {
            target_pharmacy_id: targetPharmacyId
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get check users (registered users)
 * @returns {Promise} API response
 */
export const fetchCheckUsers = async () => {
    try {
        const response = await Axios.get(check_users)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get check users assigned to a paycheck company.
 * @param {number|string} companyId - Paycheck company ID
 * @returns {Promise} API response
 */
export const fetchCheckUsersByCompany = async (companyId) => {
    try {
        const response = await Axios.get(`${check_users_by_company}/${companyId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get payroll checks list with filters
 * @param {number} companyId - Company ID
 * @param {object} filters - Filter object with year, month, status, payee_name, page, pdfdownload, exceldownload, startDate, endDate
 * @returns {Promise} API response
 */
export const fetchPayrollChecksList = async (companyId, filters = {}) => {
    try {
        const params = {
            company_id: companyId,
            ...(filters.year !== null && filters.year !== undefined && { year: filters.year }),
            ...(filters.month && { month: filters.month }),
            ...(filters.status && filters.status !== 'all' && { status: filters.status }),
            ...(filters.payee_name && { payee_name: filters.payee_name }),
            ...(filters.page && { page: filters.page }),
            ...(filters.startDate && { from: filters.startDate }),
            ...(filters.endDate && { to: filters.endDate })
        }

        // Explicitly set boolean values for download flags
        // Laravel boolean validation accepts: true, false, "true", "false", "1", "0", "on", "yes", etc.
        // For query parameters, we'll send as string "true" or "false"
        if (filters.pdfdownload !== undefined) {
            params.pdfdownload = filters.pdfdownload === true ? 1 : 0
        }
        if (filters.exceldownload !== undefined) {
            params.exceldownload = filters.exceldownload === true ? 1 : 0
        }

        const response = await Axios.get(payroll_checks_list, { params })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Update payroll check status
 * @param {number} checkId - Payroll check ID
 * @param {string} status - Status to set ('void' or 'printed')
 * @returns {Promise} API response
 */
export const updatePayrollCheckStatus = async (checkId, status) => {
    try {
        const response = await Axios.post(`${update_payroll_check_status}/${checkId}`, {
            status: status
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Generate payroll PDF for a specific check
 * @param {number} checkId - Payroll check ID
 * @returns {Promise} API response with base64 PDF data
 */
export const generatePayrollPDF = async (checkId) => {
    try {
        const response = await Axios.get(`${generate_payroll_pdf}/${checkId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Bulk generate payroll PDFs for multiple checks
 * @param {Array} payload - Array of check objects: [{id: number, status?: 'printed'}, ...]
 * @returns {Promise} API response
 */
export const bulkGeneratePayrollPDFs = async (payload) => {
    try {
        const response = await Axios.post(`${generate_payroll_pdf}/bulk`, payload)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get last generated check number for a payroll check
 * @param {number} checkId - Payroll check ID
 * @returns {Promise} API response with last_check_no and next_check_no
 */
export const getLastCheckNumber = async (checkId) => {
    try {
        const response = await Axios.get(`${get_last_check_no}/${checkId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Reissue a payroll check
 * @param {number} checkId - Payroll check ID
 * @param {object} payload - Reissue payload with amount, date, payee_name, description, memo, paycheck_companies_id, reason
 * @returns {Promise} API response
 */
export const reissuePayrollCheck = async (checkId, payload) => {
    try {
        const response = await Axios.post(`${reissue_payroll_check}/${checkId}`, payload)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get user by token (for non-seed users)
 * @returns {Promise} API response with user data (name, phone, etc.)
 */
export const getUserByToken = async () => {
    try {
        const response = await Axios.get(get_user_by_token)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Update user by token (for non-seed users)
 * @param {FormData} formData - FormData object containing name, phone, and optionally signature
 * @returns {Promise} API response
 */
export const updateUserByToken = async (formData) => {
    try {
        // Content-Type header will be set automatically by the interceptor for FormData on bookkeepers page
        const response = await Axios.post(update_check_user_by_token, formData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Fetch payee names for a company
 * @param {number} companyId - Company ID
 * @returns {Promise} API response with array of payee names
 */
export const fetchPayeeNames = async (companyId) => {
    try {
        const response = await Axios.get(`${get_payee_names}/${companyId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Extract payroll data from PDF
 * @param {FormData} formData - FormData object containing PDF file
 * @returns {Promise} API response with extracted payroll data
 */
export const extractPayrollPDF = async (formData) => {
    try {
        // Content-Type header will be set automatically by the interceptor for FormData on bookkeepers page
        const response = await Axios.post(extract_payroll_pdf, formData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Submit payroll signature (public endpoint)
 * @param {FormData|Object} payload - FormData (for file upload) or Object with token and signature (base64/data URI)
 * @returns {Promise} API response
 */
export const submitPayrollSignature = async (payload) => {
    try {
        // This is a public endpoint, so we use axios directly without the interceptor
        const baseURL = process.env.REACT_APP_BASE_URL || ''
        
        // If payload is FormData, send as multipart/form-data
        // If payload is an object, send as JSON
        const isFormData = payload instanceof FormData
        const config = {
            headers: {
                'Accept': 'application/json',
            }
        }
        
        if (!isFormData) {
            config.headers['Content-Type'] = 'application/json'
        }
        // Don't set Content-Type for FormData - let browser set it with boundary
        
        const response = await axios.post(`${baseURL}${submit_payroll_signature}`, payload, config)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Send signature link via SMS
 * @param {number} pharmacyCheckUserId - Pharmacy check user ID
 * @returns {Promise} API response
 */
export const sendSignatureLink = async (pharmacyCheckUserId) => {
    try {
        const response = await Axios.post(send_signature_link, {
            pharmacy_check_user_id: pharmacyCheckUserId
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get user by sign token (public endpoint)
 * @param {string} token - Sign token from URL
 * @returns {Promise} API response with user data {id, name, phone, pharmacy_id, signature_url}
 */
export const getCheckUserBySignToken = async (token) => {
    try {
        // This is a public endpoint, so we use axios directly without the interceptor
        const baseURL = process.env.REACT_APP_BASE_URL || ''
        const response = await axios.get(`${baseURL}${check_user_by_sign_token}`, {
            params: { token },
            headers: {
                'Accept': 'application/json',
            }
        })
        return response
    } catch (error) {
        throw error
    }
}










// Financial/Accounting OTP APIs (different from payroll)

/**
 * Request OTP for financial/accounting admin verification
 * @param {string} phone - Phone number (10 digits)
 * @returns {Promise} API response
 */
export const requestFinancialOTP = async (phone) => {
    try {
        const response = await Axios.post(financial_otp_request, {
            phone: phone
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Verify OTP for financial/accounting admin verification
 * @param {string} phone - Phone number (10 digits)
 * @param {string} otp - OTP code (6 digits)
 * @returns {Promise} API response
 */
export const verifyFinancialOTP = async (phone, otp) => {
    try {
        const response = await Axios.post(financial_otp_verify, {
            phone: phone,
            otp: otp
        })
        return response
    } catch (error) {
        throw error
    }
}

// Unified Bookkeeper and Finance OTP APIs

/**
 * Request unified OTP for both bookkeeper and finance admin verification
 * @param {string} phone - Phone number (10 digits)
 * @returns {Promise} API response with finance_access and bookkeeper_access flags
 */
export const requestUnifiedOTP = async (phone) => {
    try {
        const response = await Axios.post(unified_otp_request, {
            phone: phone
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Verify unified OTP for both bookkeeper and finance admin verification
 * @param {string} phone - Phone number (10 digits)
 * @param {string} otp - OTP code (6 digits)
 * @returns {Promise} API response with both finance and bookkeeper tokens
 */
export const verifyUnifiedOTP = async (phone, otp) => {
    try {
        const response = await Axios.post(unified_otp_verify, {
            phone: phone,
            otp: otp
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Create finance/accounting business
 * @param {Object} businessData - Business data object with name, alias_name, address, city, postcode, province, status, and banks array
 * @returns {Promise} API response
 */
export const createFinanceBusiness = async (businessData) => {
    try {
        const response = await Axios.post(finance_create_business, businessData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get finance/accounting businesses list
 * @returns {Promise} API response with businesses array
 */
export const fetchFinanceBusinesses = async () => {
    try {
        const response = await Axios.get(finance_get_businesses)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get finance/accounting accessible businesses (with banks)
 * @returns {Promise} API response with businesses array including banks
 */
export const fetchFinanceAccessibleBusinesses = async () => {
    try {
        const response = await Axios.get(finance_accessible_businesses)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Update finance/accounting business
 * @param {number} businessId - Business ID to update
 * @param {Object} businessData - Business data object with name, alias_name, address, city, postcode, province, status, and banks array
 * @returns {Promise} API response
 */
export const updateFinanceBusiness = async (businessId, businessData) => {
    try {
        const response = await Axios.put(`${finance_update_business}/${businessId}`, businessData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Import expenses CSV file for finance/accounting
 * @param {File} csvFile - CSV file to upload
 * @param {number} businessId - Business ID
 * @param {number} bankId - Bank ID
 * @returns {Promise} API response
 */
export const importFinanceExpenses = async (csvFile, businessId, bankId) => {
    try {
        const formData = new FormData()
        formData.append('csv_file', csvFile)
        formData.append('business_id', businessId)
        formData.append('bank_id', bankId)

        const response = await Axios.post(finance_import_expenses, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Logout finance/accounting user
 * @returns {Promise} API response
 */
export const logoutFinanceUser = async () => {
    try {
        const response = await Axios.post(finance_logout)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Create finance/accounting bank under a business
 * @param {number} businessId - Business ID
 * @param {Object} bankData - Bank data object with bank_name, type, account_number/creditcard_no/debitcard_no, status, is_primary
 * @returns {Promise} API response
 */
export const createFinanceBank = async (businessId, bankData) => {
    try {
        const response = await Axios.post(`${finance_create_business}/${businessId}/banks`, bankData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Delete finance/accounting bank
 * @param {number} bankId - Bank ID to delete
 * @returns {Promise} API response
 */
export const deleteFinanceBank = async (bankId) => {
    try {
        const response = await Axios.delete(`${finance_delete_bank}/${bankId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Export business and bank details to bookkeeper
 * @param {FormData} formData - Form data with business and bank details
 * @returns {Promise} API response
 */
export const exportBusinessToBookkeeper = async (formData) => {
    try {
        const response = await Axios.post(finance_export_to_bookkeeper, formData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Fetch finance/accounting transactions
 * @param {number} bankId - Bank ID
 * @param {number} page - Page number (default: 1)
 * @param {string} startDate - Start date (format: YYYY-MM-DD, optional)
 * @param {string} endDate - End date (format: YYYY-MM-DD, optional)
 * @returns {Promise} API response with transactions data
 */
export const fetchFinanceTransactions = async (bankId, page = 1, startDate = null, endDate = null, billType = null) => {
    try {
        const params = {
            bank_id: bankId,
            page: page
        }
        
        if (startDate) {
            params.start_date = startDate
        }
        
        if (endDate) {
            params.end_date = endDate
        }
        
        if (billType) {
            params.bill_type = billType
        }
        
        const response = await Axios.get(finance_transactions, {
            params
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Fetch finance/accounting chart data (top spending categories and monthly spending trend)
 * @param {number} bankId - Bank ID
 * @param {string} startDate - Start date (format: YYYY-MM-DD)
 * @param {string} endDate - End date (format: YYYY-MM-DD)
 * @returns {Promise} API response with chart data
 */
export const fetchFinanceChartData = async (bankId, startDate = null, endDate = null) => {
    try {
        const params = {
            bank_id: bankId
        }
        
        if (startDate) {
            params.start_date = startDate
        }
        
        if (endDate) {
            params.end_date = endDate
        }
        
        const response = await Axios.get(finance_transactions_chart_data, {
            params
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Update transaction memo
 * @param {number} transactionId - Transaction ID
 * @param {string} memo - Memo text
 * @returns {Promise} API response
 */
export const updateTransactionMemo = async (transactionId, memo) => {
    try {
        const response = await Axios.patch(`${finance_transactions_update_memo}/${transactionId}/memo`, {
            memo: memo || ''
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Fetch transaction categories
 * @returns {Promise} API response with categories list
 */
export const fetchFinanceTransactionCategories = async () => {
    try {
        const response = await Axios.get(`${finance_transactions}/categories`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Update transaction details (category, GST, PST)
 * @param {number} transactionId - Transaction ID
 * @param {Object} details - Details object with gst, pst, and category_id or category_name
 * @returns {Promise} API response
 */
export const updateTransactionDetails = async (transactionId, details) => {
    try {
        const response = await Axios.patch(`${finance_transactions}/${transactionId}/details`, details)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get bill info by transaction ID
 * @param {number} transactionId - Transaction ID
 * @returns {Promise} API response with bill info including bill_image_base64
 */
export const fetchTransactionBill = async (transactionId) => {
    try {
        const response = await Axios.get(`${finance_transactions}/${transactionId}/bill`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get bills by bank ID for finance/accounting
 * @param {number} bankId - Bank ID
 * @returns {Promise} API response
 */
export const fetchFinanceBillsByBank = async (bankId) => {
    try {
        const response = await Axios.get(`${finance_bills}/by-bank`, {
            params: {
                bank_id: bankId
            }
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Upload bill image for finance/accounting
 * @param {number} businessId - Business ID
 * @param {number} bankId - Bank ID
 * @param {File} billImage - Bill image file
 * @param {number} transactionId - Transaction ID (optional)
 * @returns {Promise} API response
 */
export const uploadFinanceBill = async (businessId, bankId, billImage, transactionId = null) => {
    try {
        const formData = new FormData()
        formData.append('business_id', businessId)
        formData.append('bank_id', bankId)
        formData.append('bill_image', billImage)
        if (transactionId) {
            formData.append('transaction_id', transactionId)
        }

        // Content-Type header will be set automatically by the interceptor for FormData on upload-bills page
        const response = await Axios.post(finance_bills, formData)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Attach a bill to a transaction
 * Can accept either a bill_id (for existing bills) or a file (for new uploads)
 * @param {number} transactionId - Transaction ID
 * @param {number|File} billIdOrFile - Bill ID (number) for existing bills, or File object for new uploads
 * @param {number} businessId - Business ID (required when uploading file)
 * @param {number} bankId - Bank ID (required when uploading file)
 * @returns {Promise} API response
 */
export const attachBillToTransaction = async (transactionId, billIdOrFile, businessId = null, bankId = null) => {
    try {
        // If billIdOrFile is a File object, send as FormData
        if (billIdOrFile instanceof File) {
            if (!businessId || !bankId) {
                throw new Error('Business ID and Bank ID are required when uploading a file')
            }
            
            const formData = new FormData()
            formData.append('transaction_id', transactionId)
            formData.append('bill_image', billIdOrFile)
            formData.append('business_id', businessId)
            formData.append('bank_id', bankId)
            
            const response = await Axios.post(finance_bills_attach_to_transaction, formData)
            return response
        } else {
            // If billIdOrFile is a number (bill_id), send as JSON
            const response = await Axios.post(finance_bills_attach_to_transaction, {
                transaction_id: transactionId,
                bill_id: billIdOrFile
            })
            return response
        }
    } catch (error) {
        throw error
    }
}

// Accounting Access Form APIs
/**
 * Get accounting access users (registered users)
 * @returns {Promise} API response
 */
export const fetchAccountingUsers = async () => {
    try {
        const response = await Axios.get(accounting_access_form)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get a single accounting user by ID
 * @param {number} userId - User ID
 * @returns {Promise} API response
 */
export const fetchAccountingUserById = async (userId) => {
    try {
        const response = await Axios.get(`${accounting_access_form}/${userId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Update accounting user
 * @param {number} userId - User ID to update
 * @param {Object|FormData} data - Form data with name, phone, business_ids
 * @returns {Promise} API response
 */
export const updateAccountingUser = async (userId, data) => {
    try {
        const response = await Axios.put(`${accounting_access_form}/${userId}`, data)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Delete accounting user
 * @param {number} userId - User ID to delete
 * @returns {Promise} API response
 */
export const deleteAccountingUser = async (userId) => {
    try {
        const response = await Axios.delete(`${accounting_access_form}/${userId}`)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Get current finance user
 * @returns {Promise} API response
 */
export const fetchFinanceCurrentUser = async () => {
    try {
        const response = await Axios.get(finance_current_user)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Update current finance user
 * @param {number} userId - User ID to update
 * @param {Object|FormData} data - Form data with name, phone
 * @returns {Promise} API response
 */
export const updateFinanceCurrentUser = async (userId, data) => {
    try {
        const response = await Axios.put(`${accounting_access_form}/${userId}`, data)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Submit accounting access form
 * @param {Object} data - Form data with name, phone, business
 * @returns {Promise} API response
 */
export const submitAccountingAccessForm = async (data) => {
    try {
        const response = await Axios.post(accounting_access_form, data)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Fetch linked pharmacies for accounting access (direct)
 * @returns {Promise} API response
 */
export const fetchLinkedPharmaciesAccountingDirect = async () => {
    try {
        const response = await Axios.get(finance_linked_pharmacies_direct)
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Link pharmacy for accounting access
 * @param {number} targetPharmacyId - Target pharmacy ID to link with
 * @returns {Promise} API response
 */
export const linkPharmacyAccounting = async (targetPharmacyId) => {
    try {
        const response = await Axios.post(finance_link_pharmacy, {
            target_pharmacy_id: targetPharmacyId
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Unlink pharmacy for accounting access
 * @param {number} targetPharmacyId - Target pharmacy ID to unlink
 * @returns {Promise} API response
 */
export const unlinkPharmacyAccounting = async (targetPharmacyId) => {
    try {
        const response = await Axios.post(finance_unlink_pharmacy, {
            target_pharmacy_id: targetPharmacyId
        })
        return response
    } catch (error) {
        throw error
    }
}

/**
 * Export finance/accounting transactions as CSV
 * @param {number} bankId - Bank ID
 * @param {string} startDate - Start date (format: YYYY-MM-DD, optional)
 * @param {string} endDate - End date (format: YYYY-MM-DD, optional)
 * @param {string} billType - Bill type filter ('mapped', 'non_mapped', optional)
 * @returns {Promise} API response with filename, file_data (base64), file_type, total_transactions
 */
export const exportFinanceTransactionsCSV = async (bankId, startDate = null, endDate = null, billType = null) => {
    try {
        const params = {
            bank_id: bankId
        }
        
        if (startDate) {
            params.start_date = startDate
        }
        
        if (endDate) {
            params.end_date = endDate
        }
        
        if (billType) {
            params.bill_type = billType
        }
        
        const response = await Axios.get(finance_transactions_export_csv, {
            params
        })
        return response
    } catch (error) {
        throw error
    }
}
